import { Fragment, useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { FilterField } from '@/components/ui/FilterBar';
import { useToast } from '@/components/ui/useToast';
import { useAuth } from '@/auth/useAuth';
import { apiError } from '@/lib/apiError';
import { getExpBudget, getExpBudgets, getExpDepartments, getExpHeads, getExpMonthly, getExpRegister, saveExpBudgets } from '@/api/accountsExp';
import { getTdsDeductions, type TdsDeduction } from '@/api/accountsTds';
import type { ExportCell } from '../exportReport';
import { VoucherFormModal } from '../books/VoucherFormModal';
import { Check, LedgerVouchersView, Loading, PeriodBar, ReportFrame } from '../reports/reportParts';
import { NO_ACTS, amt, num, r2, td, tdate, th, useExcel, useKeyNav, usePeriod } from '../reports/reportUtils';
import { fyOf, monthLabel } from '../tds/tdsUtils';
import { useVoucherOpen } from '../tds/useVoucherOpen';

/* The Expenses Reports group of the prototype, in TallyPrime's screen formats: the expense bills as
   a Journal Register, the expense heads (Group Summary, monthly columns), departments (cost centres),
   Budget Variance, and the TDS deducted on expenses by nature of payment. A head opens its ledger. */

const key = (k: string, ...x: unknown[]) => ['acc-books', 'exp', k, ...x];
const pct = (v: number | null) => (v == null || Math.abs(v) < 0.005 ? '' : `${v.toFixed(2)}%`);
const period = (p: { from: string; to: string }) => `${tdate(p.from)} to ${tdate(p.to)}`;

/** A table with the account reports' header row (Particulars + money columns). */
function Head({ cols, first = 'Particulars' }: { cols: string[]; first?: string }) {
  return (
    <thead>
      <tr className="border-b border-[var(--border-color)]">
        <th className={clsx(th, 'text-left')}>{first}</th>
        {cols.map((c) => <th key={c} className={clsx(th, 'text-right')}>{c}</th>)}
      </tr>
    </thead>
  );
}
const Empty = ({ cols, text = 'Nothing in this period.' }: { cols: number; text?: string }) => <tr><td colSpan={cols} className={clsx(td, 'py-6 text-center text-[var(--text-muted)]')}>{text}</td></tr>;
const Total = ({ label, cells }: { label: string; cells: (string | number)[] }) => (
  <tfoot><tr className="border-t-2 border-[var(--text-primary)] font-extrabold"><td className={td}>{label}</td>{cells.map((c, i) => <td key={i} className={num}>{typeof c === 'number' ? amt(c) : c}</td>)}</tr></tfoot>
);

/** A head's ledger vouchers for the period (Esc / Back returns to the report). */
function useLedgerDrill() {
  const [p] = usePeriod();
  const [led, setLed] = useState<number | null>(null);
  const open = useCallback((id: number) => () => setLed(id), []);
  const view = led != null ? <LedgerVouchersView ledgerId={led} from={p.from} to={p.to} onBack={() => setLed(null)} /> : null;
  return { view, drilled: led != null, open };
}

// ── Expense Entry ──

export function ExpEntryPage() {
  const [p] = usePeriod();
  const q = useQuery({ queryKey: key('register', p.from, p.to), queryFn: () => getExpRegister(p) });
  const [head, setHead] = useState('');
  const [det, setDet] = useState(false);
  const [adding, setAdding] = useState(false);
  const canBook = useAuth().hasPerm('acc-journal');
  const v = useVoucherOpen();
  const excel = useExcel();
  const all = useMemo(() => q.data?.rows ?? [], [q.data]);
  const heads = useMemo(() => [...new Set(all.map((r) => r.head))].sort(), [all]);
  const rows = useMemo(() => all.filter((r) => !head || r.head === head), [all, head]);
  const vOpen = v.open;
  const acts = useMemo(() => rows.map((r) => vOpen(r.voucherId)), [rows, vOpen]);
  const { row } = useKeyNav(acts);
  const sum = (f: (r: (typeof rows)[number]) => number) => r2(rows.reduce((s, r) => s + f(r), 0));
  return (
    <>
      <PeriodBar onExcel={() => excel('Expense_Entry', 'Journal Register', ['Date', 'Expense Head', 'Party', 'Bill No.', 'Department', 'Vch Type', 'Vch No.', 'Amount', 'GST', 'GST not claimable', 'TDS', 'Total Cost'],
        rows.map((r) => [tdate(r.date), r.head, r.party, r.billNo ?? '', r.dept ?? '', r.type, r.voucherNo, r.taxable, r.gst, r.inelig, r.tds, r.cost] as ExportCell[]))}>
        <FilterField label="Expense Head"><Select value={head} onChange={(e) => setHead(e.target.value)}><option value="">All</option>{heads.map((h) => <option key={h}>{h}</option>)}</Select></FilterField>
        <Check label="Detailed (show entries)" checked={det} onChange={setDet} />
        {canBook && <div className="col-span-3 sm:col-auto sm:self-end"><Button variant="primary" size="sm" onClick={() => setAdding(true)}>＋ Expense bill</Button></div>}
      </PeriodBar>
      <Loading q={q}>
        <ReportFrame title={`Journal Register${head ? ` · ${head}` : ''}`} right={period(p)} note="Every expense bill (Journal, or Payment when there's no vendor). Click a voucher to open it — or use ↑ ↓ and Enter.">
          <table className="w-full min-w-[640px] text-xs">
            <thead>
              <tr className="border-b border-[var(--border-color)] text-left">
                <th className={th}>Date</th><th className={th}>Particulars</th><th className={th}>Vch Type</th><th className={th}>Vch No.</th>
                <th className={`${th} text-right`}>Debit Amount</th><th className={`${th} text-right`}>Credit Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <Fragment key={r.voucherId}>
                  <tr {...row(i)}>
                    <td className={`${td} whitespace-nowrap`}>{tdate(r.date)}</td>
                    <td className={td}><span className="font-semibold">{r.head}</span><div className="text-[11px] text-[var(--text-muted)]">{[r.party, r.billNo && `Bill ${r.billNo}`, r.dept].filter(Boolean).join(' · ')}</div></td>
                    <td className={td}>{r.type}</td>
                    <td className={`${td} whitespace-nowrap font-mono`}>{r.voucherNo}</td>
                    <td className={num}>{amt(r.cost)}</td><td className={num} />
                  </tr>
                  {det && r.lines.map((l, j) => (
                    <tr key={j} className="border-b border-[var(--border-color)] text-[11px] italic text-[var(--text-secondary)]">
                      <td /><td className={`${td} pl-6`}>{l.amount > 0 ? 'Dr' : 'Cr'}&nbsp; {l.ledger}</td><td /><td />
                      <td className={num}>{l.amount > 0 ? amt(l.amount) : ''}</td><td className={num}>{l.amount < 0 ? amt(l.amount) : ''}</td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              {!rows.length && <Empty cols={6} text="No expense bills between these dates." />}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="border-t border-[var(--border-color)] text-[11px] text-[var(--text-secondary)]">
                  <td className={td} colSpan={6}>{rows.length} bill{rows.length === 1 ? '' : 's'} · Amount ₹{amt(sum((r) => r.taxable)) || '0.00'} · GST ₹{amt(sum((r) => r.gst)) || '0.00'} · TDS ₹{amt(sum((r) => r.tds)) || '0.00'}</td>
                </tr>
                <tr className="border-t-2 border-[var(--text-primary)] font-extrabold"><td className={td} colSpan={4}>Total</td><td className={num}>{amt(sum((r) => r.cost))}</td><td /></tr>
              </tfoot>
            )}
          </table>
        </ReportFrame>
      </Loading>
      {adding && <VoucherFormModal type="Journal" voucher={null} startExpense onClose={() => setAdding(false)} />}
      {v.el}
    </>
  );
}

// ── Head-wise ──

export function ExpHeadPage() {
  const [p] = usePeriod();
  const q = useQuery({ queryKey: key('heads', p.from, p.to), queryFn: () => getExpHeads(p) });
  const d = useLedgerDrill();
  const rows = useMemo(() => q.data?.rows ?? [], [q.data]);
  const { drilled, open } = d;
  const acts = useMemo(() => (drilled ? NO_ACTS : rows.map((r) => open(r.ledgerId))), [rows, drilled, open]);
  const { row } = useKeyNav(acts);
  const excel = useExcel();
  if (d.view) return d.view;
  const T = (k: 'opening' | 'current' | 'gst' | 'tds' | 'cost') => r2(rows.reduce((s, r) => s + r[k], 0));
  return (
    <>
      <PeriodBar onExcel={() => excel('Expense_Head_wise', 'Head-wise', ['Expense Head', 'Opening', 'Current Period', 'GST', 'TDS', 'Total Expense', '% of Total'], rows.map((r) => [r.head, r.opening, r.current, r.gst, r.tds, r.cost, r.share] as ExportCell[]))} />
      <Loading q={q}>
        <ReportFrame title="Group Summary · Indirect Expenses" right={period(p)} note="Every voucher posted to the expense heads. Opening = spent earlier this year, before From. Total Expense = amount + GST that can’t be claimed. Click a head for its vouchers.">
          <table className="w-full min-w-[720px] text-xs">
            <Head cols={['Opening', 'Current Period', 'GST', 'TDS', 'Total Expense', '% of Total']} />
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.ledgerId} {...row(i)}>
                  <td className={`${td} font-semibold`}>{r.head}</td>
                  <td className={num}>{amt(r.opening)}</td><td className={num}>{amt(r.current)}</td><td className={num}>{amt(r.gst)}</td><td className={num}>{amt(r.tds)}</td>
                  <td className={`${num} font-bold`}>{amt(r.cost)}</td><td className={num}>{pct(r.share)}</td>
                </tr>
              ))}
              {!rows.length && <Empty cols={7} />}
            </tbody>
            {rows.length > 0 && <Total label="Grand Total" cells={[T('opening'), T('current'), T('gst'), T('tds'), T('cost'), T('cost') ? '100.00%' : '']} />}
          </table>
        </ReportFrame>
      </Loading>
    </>
  );
}

// ── Monthly ──

export function ExpMonthPage() {
  const [p] = usePeriod();
  const q = useQuery({ queryKey: key('monthly', p.from, p.to), queryFn: () => getExpMonthly(p) });
  const d = useLedgerDrill();
  const rows = useMemo(() => q.data?.rows ?? [], [q.data]), months = q.data?.months ?? [];
  const { drilled, open } = d;
  const acts = useMemo(() => (drilled ? NO_ACTS : rows.map((r) => open(r.ledgerId))), [rows, drilled, open]);
  const { row } = useKeyNav(acts);
  const excel = useExcel();
  if (d.view) return d.view;
  return (
    <>
      <PeriodBar onExcel={() => excel('Monthly_Expense', 'Monthly', ['Expense Head', ...months.map(monthLabel), 'Total'], rows.map((r) => [r.head, ...r.months, r.total] as ExportCell[]))} />
      <Loading q={q}>
        <ReportFrame title="Group Summary · Indirect Expenses · Monthly" right={period(p)} note="Total expense per head per month (GST that can’t be claimed included). Click a head for its vouchers.">
          <table className="w-full text-xs" style={{ minWidth: 220 + (months.length + 1) * 100 }}>
            <Head cols={[...months.map(monthLabel), 'Total']} />
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.ledgerId} {...row(i)}>
                  <td className={`${td} font-semibold`}>{r.head}</td>
                  {r.months.map((x, j) => <td key={j} className={num}>{amt(x)}</td>)}
                  <td className={`${num} font-bold`}>{amt(r.total)}</td>
                </tr>
              ))}
              {!rows.length && <Empty cols={months.length + 2} />}
            </tbody>
            {rows.length > 0 && <Total label="Grand Total" cells={[...months.map((_, j) => r2(rows.reduce((s, r) => s + r.months[j], 0))), r2(rows.reduce((s, r) => s + r.total, 0))]} />}
          </table>
        </ReportFrame>
      </Loading>
    </>
  );
}

// ── Department-wise ──

export function ExpDeptPage() {
  const [p] = usePeriod();
  const q = useQuery({ queryKey: key('departments', p.from, p.to), queryFn: () => getExpDepartments(p) });
  const ld = useLedgerDrill();
  const [det, setDet] = useState(true);
  const [dept, setDept] = useState<string | null>(null);
  const excel = useExcel();
  const all = useMemo(() => q.data?.rows ?? [], [q.data]);
  const shown = useMemo(() => (dept ? all.filter((r) => r.dept === dept) : all), [all, dept]);
  const { drilled, open } = ld;
  // Lines: a department, then its heads (when Detailed, or drilled into one department).
  const lines = useMemo(() => shown.flatMap((r) => [
    ...(dept ? [] : [{ kind: 'dept' as const, r, go: () => setDept(r.dept) }]),
    ...(dept || det ? r.heads.map((h) => ({ kind: 'head' as const, r: h, go: open(h.ledgerId) })) : []),
  ]), [shown, dept, det, open]);
  const acts = useMemo(() => (drilled ? NO_ACTS : lines.map((l) => l.go)), [lines, drilled]);
  const back = useCallback(() => setDept(null), []);
  const { row } = useKeyNav(acts, dept && !drilled ? back : undefined);
  if (ld.view) return ld.view;
  const T = (k: 'vouchers' | 'amount' | 'gst' | 'tds' | 'cost') => r2(shown.reduce((s, r) => s + r[k], 0));
  const cells = (x: { vouchers: number; amount: number; gst: number; tds: number; cost: number }, b = false) => (
    <><td className={num}>{x.vouchers || ''}</td><td className={num}>{amt(x.amount)}</td><td className={num}>{amt(x.gst)}</td><td className={num}>{amt(x.tds)}</td><td className={clsx(num, b && 'font-bold')}>{amt(x.cost)}</td></>
  );
  return (
    <>
      <PeriodBar onBack={dept ? () => setDept(null) : undefined} onExcel={() => excel('Department_wise_Expense', 'Departments', ['Department', 'Expense Head', 'Vouchers', 'Amount', 'GST', 'TDS', 'Total Cost'],
        shown.flatMap((r) => [[r.dept, '', r.vouchers, r.amount, r.gst, r.tds, r.cost] as ExportCell[], ...r.heads.map((h) => ['', h.head, h.vouchers, h.amount, h.gst, h.tds, h.cost] as ExportCell[])]))}>
        {!dept && <Check label="Detailed (show expense heads)" checked={det} onChange={setDet} />}
      </PeriodBar>
      <Loading q={q}>
        <ReportFrame title={dept ? `Cost Centre Break-up · ${dept}` : 'Category Summary · Departments'} right={period(p)} note={dept ? 'Click a head for its vouchers — Esc to go back.' : 'Departments are the cost centres on the expense lines. Click a department for its break-up — or use ↑ ↓ and Enter.'}>
          <table className="w-full min-w-[640px] text-xs">
            <Head cols={['Vouchers', 'Amount', 'GST', 'TDS', 'Total Cost']} />
            <tbody>
              {lines.map((l, i) => (
                <tr key={`${l.kind}-${l.kind === 'dept' ? l.r.dept : `${(l.r as { ledgerId: number }).ledgerId}-${i}`}`} {...row(i, l.kind === 'dept' ? 'font-semibold' : '')}>
                  <td className={clsx(td, l.kind === 'head' && !dept && 'pl-6 text-[var(--text-secondary)]', l.kind === 'head' && dept && 'font-semibold')}>{l.kind === 'dept' ? l.r.dept : (l.r as { head: string }).head}</td>
                  {cells(l.r, l.kind === 'dept')}
                </tr>
              ))}
              {!lines.length && <Empty cols={6} />}
            </tbody>
            {shown.length > 0 && <Total label="Grand Total" cells={[T('vouchers') || '', T('amount'), T('gst'), T('tds'), T('cost')]} />}
          </table>
        </ReportFrame>
      </Loading>
    </>
  );
}

// ── Expense vs Budget ──

const tone = (s: string) => (s === 'Over budget' ? 'bg-danger/15 text-danger' : s === 'Within budget' ? 'bg-success/15 text-success' : s === 'Under-utilised' ? 'bg-accent/15 text-accent' : 'bg-warning/15 text-warning');

export function ExpBudgetPage() {
  const [p] = usePeriod();
  const q = useQuery({ queryKey: key('budget', p.from, p.to), queryFn: () => getExpBudget(p) });
  const d = useLedgerDrill();
  const [editing, setEditing] = useState(false);
  const rows = useMemo(() => q.data?.rows ?? [], [q.data]);
  const { drilled, open } = d;
  const acts = useMemo(() => (drilled ? NO_ACTS : rows.map((r) => open(r.ledgerId))), [rows, drilled, open]);
  const { row } = useKeyNav(acts);
  const excel = useExcel();
  if (d.view) return d.view;
  const A = r2(rows.reduce((s, r) => s + r.actual, 0)), B = r2(rows.reduce((s, r) => s + r.budget, 0)), V = r2(A - B);
  const vcell = (x: number, b = false) => <td className={clsx(num, b && 'font-bold', x > 0.004 ? 'text-danger' : x < -0.004 ? 'text-success' : '')}>{Math.abs(x) > 0.004 ? `${x > 0 ? '+' : '−'}${amt(x)}` : ''}</td>;
  const k = q.data?.months ?? 0;
  return (
    <>
      <PeriodBar onExcel={() => excel('Expense_vs_Budget', 'Budget Variance', ['Expense Head', 'Actuals', 'Budget', 'Variance', 'Variance %', 'Status'], rows.map((r) => [r.head, r.actual, r.budget, r.variance, r.pct, r.status] as ExportCell[]))}>
        <div className="col-span-3 flex items-center gap-2 sm:col-auto sm:self-end">
          <span className="text-[11px] text-[var(--text-muted)]">Budget for {k} month{k === 1 ? '' : 's'}</span>
          <Button variant="glass" size="sm" onClick={() => setEditing(true)}>Set budgets</Button>
        </div>
      </PeriodBar>
      <Loading q={q}>
        <ReportFrame title="Budget Variance · Indirect Expenses" right={period(p)} note="Budget = monthly budget × months in the period. More than 5% over is flagged; 15% or more under is under-utilised. Click a head for its vouchers.">
          <table className="w-full min-w-[680px] text-xs">
            <Head cols={['Actuals', 'Budget', 'Variance', 'Variance %', 'Status']} />
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.ledgerId} {...row(i)}>
                  <td className={`${td} font-semibold`}>{r.head}</td>
                  <td className={num}>{amt(r.actual)}</td><td className={num}>{amt(r.budget)}</td>{vcell(r.variance)}
                  <td className={num}>{r.pct == null ? '' : `${r.pct > 0 ? '+' : ''}${r.pct.toFixed(1)}%`}</td>
                  <td className={`${td} text-right`}><span className={clsx('whitespace-nowrap rounded-full px-2 py-0.5 text-[10.5px] font-bold', tone(r.status))}>{r.status}</span></td>
                </tr>
              ))}
              {!rows.length && <Empty cols={6} text="No expenses or budgets in this period — set budgets to compare." />}
            </tbody>
            {rows.length > 0 && (
              <tfoot><tr className="border-t-2 border-[var(--text-primary)] font-extrabold">
                <td className={td}>Grand Total</td><td className={num}>{amt(A)}</td><td className={num}>{amt(B)}</td>{vcell(V, true)}<td className={num}>{B ? `${V > 0 ? '+' : ''}${((V / B) * 100).toFixed(1)}%` : ''}</td><td />
              </tr></tfoot>
            )}
          </table>
        </ReportFrame>
      </Loading>
      {editing && <BudgetModal onClose={() => setEditing(false)} />}
    </>
  );
}

/** Budgets: a monthly amount per expense head (blank = none). */
function BudgetModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient(); const toast = useToast();
  const q = useQuery({ queryKey: key('budgets'), queryFn: getExpBudgets });
  const [draft, setDraft] = useState<Record<number, string>>({});
  const [search, setSearch] = useState('');
  const val = (id: number, saved: number) => draft[id] ?? (saved ? String(saved) : '');
  const changed = (q.data ?? []).filter((b) => draft[b.ledgerId] != null && Number(draft[b.ledgerId] || 0) !== b.monthly);
  const save = useMutation({
    mutationFn: () => saveExpBudgets(changed.map((b) => ({ ledgerId: b.ledgerId, monthly: Number(draft[b.ledgerId] || 0) }))),
    onSuccess: () => { toast.push('Budgets saved', 'success'); qc.invalidateQueries({ queryKey: ['acc-books', 'exp'] }); onClose(); },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });
  const list = (q.data ?? []).filter((b) => !search.trim() || b.head.toLowerCase().includes(search.trim().toLowerCase()));
  const total = r2((q.data ?? []).reduce((s, b) => s + Number(val(b.ledgerId, b.monthly) || 0), 0));
  return (
    <Modal open onClose={onClose} title="Budgets — per month" size="md"
      footer={<><Button variant="glass" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={save.isPending || !changed.length} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : `Save${changed.length ? ` (${changed.length})` : ''}`}</Button></>}>
      <Loading q={q}>
        <Input className="mb-2" placeholder="Search expense head…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="max-h-[55vh] overflow-y-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b border-[var(--border-color)]"><th className={clsx(th, 'text-left')}>Expense Head</th><th className={clsx(th, 'w-36 text-right')}>Budget / month</th></tr></thead>
            <tbody>
              {list.map((b) => (
                <tr key={b.ledgerId} className="border-b border-[var(--border-color)]">
                  <td className={td}>{b.head}{b.updatedBy && <div className="text-[10px] text-[var(--text-muted)]">set by {b.updatedBy}</div>}</td>
                  <td className="px-2 py-1"><Input type="number" min={0} step="0.01" inputMode="decimal" className="text-right font-mono" placeholder="—" value={val(b.ledgerId, b.monthly)} onChange={(e) => setDraft({ ...draft, [b.ledgerId]: e.target.value })} /></td>
                </tr>
              ))}
              {!list.length && <Empty cols={2} text="No expense heads — create ledgers under Indirect Expenses." />}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-[11px] text-[var(--text-muted)]">Total per month ₹{amt(total) || '0.00'}. Leave a head blank (or 0) for no budget.</p>
      </Loading>
    </Modal>
  );
}

// ── TDS on expenses, by nature of payment ──

function TdsNature({ title, right, pick, reason }: { title: string; right: string; pick: (section: string) => boolean; reason?: boolean }) {
  const [p] = usePeriod();
  const q = useQuery({ queryKey: ['acc-books', 'tds', 'deductions', 'range', p.from, p.to], queryFn: () => getTdsDeductions({ fy: fyOf(p.to), from: p.from, to: p.to }) });
  const v = useVoucherOpen();
  const excel = useExcel();
  const rows = useMemo(() => (q.data?.rows ?? []).filter((r) => pick(r.section)), [q.data, pick]);
  // Lines: a section subtotal (nothing to open), then its entries.
  const lines = useMemo(() => [...new Set(rows.map((r) => r.section))].sort().flatMap((s) => {
    const g = { s, list: rows.filter((r) => r.section === s) };
    return [{ g, r: null as TdsDeduction | null }, ...g.list.map((r) => ({ g, r }))];
  }), [rows]);
  const vOpen = v.open;
  const acts = useMemo(() => lines.map((l) => (l.r ? vOpen(l.r.voucherId) : null)), [lines, vOpen]);
  const { row } = useKeyNav(acts);
  const S = (xs: TdsDeduction[], f: (r: TdsDeduction) => number) => r2(xs.reduce((s, r) => s + f(r), 0));
  return (
    <>
      <PeriodBar onExcel={() => excel(title.replace(/[^A-Za-z0-9]+/g, '_'), 'TDS Deducted', ['Section', 'Nature of Payment', 'Date', 'Party', 'PAN', 'Vch No.', 'Assessable Value', 'Rate %', 'TDS Amount', ...(reason ? ['Basis'] : [])],
        rows.map((r) => [r.section, r.nature, tdate(r.date), r.party, r.pan ?? 'No PAN', r.voucherNo, r.base, r.rate, r.tds, ...(reason ? [r.reason ?? ''] : [])] as ExportCell[]))} />
      <Loading q={q}>
        <ReportFrame title={`TDS Deducted · ${right}`} right={period(p)} note="Click an entry to open its voucher — or use ↑ ↓ and Enter.">
          <table className="w-full min-w-[640px] text-xs">
            <thead>
              <tr className="border-b border-[var(--border-color)] text-left">
                <th className={th}>Date</th><th className={th}>Particulars</th><th className={th}>Vch No.</th>
                <th className={`${th} text-right`}>Assessable Value</th><th className={`${th} text-right`}>Rate</th><th className={`${th} text-right`}>TDS Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (l.r ? (
                <tr key={`${l.r.voucherId}-${i}`} {...row(i)}>
                  <td className={`${td} whitespace-nowrap`}>{tdate(l.r.date)}</td>
                  <td className={td}><span className="font-semibold">{l.r.party}</span><div className="text-[11px] text-[var(--text-muted)]">PAN {l.r.pan ?? '—'}{reason && l.r.reason ? ` · ${l.r.reason}` : ''}</div></td>
                  <td className={`${td} whitespace-nowrap font-mono`}>{l.r.voucherNo}</td>
                  <td className={num}>{amt(l.r.base)}</td><td className={num}>{l.r.rate}%</td><td className={num}>{amt(l.r.tds)}</td>
                </tr>
              ) : (
                <tr key={`s-${l.g.s}`} {...row(i, 'bg-accent/5 font-semibold')}>
                  <td className={td} colSpan={3}>{l.g.list[0].nature} <span className="text-[11px] font-normal text-[var(--text-muted)]">· Section {l.g.s}</span></td>
                  <td className={num}>{amt(S(l.g.list, (r) => r.base))}</td><td className={num} /><td className={num}>{amt(S(l.g.list, (r) => r.tds))}</td>
                </tr>
              )))}
              {!lines.length && <Empty cols={6} text="No TDS deducted between these dates." />}
            </tbody>
            {rows.length > 0 && <Total label={`Grand Total · ${rows.length} entr${rows.length === 1 ? 'y' : 'ies'}`} cells={['', '', S(rows, (r) => r.base), '', S(rows, (r) => r.tds)]} />}
          </table>
        </ReportFrame>
      </Loading>
      {v.el}
    </>
  );
}

const isProf = (s: string) => s === '1009', isCon = (s: string) => s === '1024', isOther = (s: string) => !isProf(s) && !isCon(s);
export const ExpTdsProfPage = () => <TdsNature title="TDS on Professional Charges" right="Professional Charges" pick={isProf} />;
export const ExpTdsConPage = () => <TdsNature title="TDS on Contractor Payment" right="Contractor Payments" pick={isCon} reason />;
export const ExpTdsOtherPage = () => <TdsNature title="Other Applicable TDS Entries" right="Other Sections" pick={isOther} />;
